import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import {
  CalendarClock,
  Eye,
  Globe2,
  MessageSquareText,
  Plus,
  Send,
  Star,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import { CONTINENT_OPTIONS, toggleCode } from "../contacts/geography";
import {
  cohortFilterLabelKeys,
  facetCount,
  useContactFilterFacets,
} from "../contacts/cohortFilters";
import {
  broadcastAudienceInputs,
  broadcastEditDraft,
} from "./broadcastEditDraft";
import { ButtonIntentHint } from "./ButtonIntentHint";
import { WhatsAppMessageBubble } from "./WhatsAppMessageBubble";
import {
  getHeaderMediaFormat,
  getTemplateButtons,
  renderTemplateMessageParts,
  unfilledPlaceholders,
} from "./whatsappMessagePreview";
import { broadcastPreviewInvalidationKey } from "./broadcastPreviewInvalidationKey";
import {
  applyRatingToCohortFilter,
  buildContactRatingListFilter,
  normalizeSelectedRatings,
  RATING_STAR_OPTIONS,
} from "./ratingListFilter";
import {
  CONTACT_TEMPLATE_VARIABLES,
  contactVariableToken,
  describeVariableTokens,
} from "./contactTemplateVariables";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { CrmDataProvider } from "../providers/types";
import type {
  ContactAudienceSelection,
  ContactCohortFilter,
  ContactGeoCode,
  WhatsAppBroadcast,
  WhatsAppBroadcastPreview,
  WhatsAppBroadcastPreviewRecipient,
  WhatsAppTemplate,
  WhatsAppTemplateMediaAsset,
  WhatsAppSenderCatalog,
} from "../types";
import {
  ContactAudienceSelector,
  type ContactAudienceEstimate,
} from "./ContactAudienceSelector";
import {
  buildContactGeographyListFilter,
  buildExcludedContactGeographyListFilter,
} from "./geographyListFilter";
import {
  BroadcastHeaderMediaField,
  type BroadcastHeaderMediaValue,
  defaultHeaderMediaValue,
} from "./BroadcastHeaderMediaField";
import {
  fileToBase64,
  isMediaHeaderField,
  type MediaHeaderFormat,
} from "./templateMedia";
import {
  initialSenderKey,
  senderCanSend,
  senderLabel,
  templateMatchesSender,
} from "./whatsappSenderSelection";

export function WhatsAppBroadcastDialog({
  open,
  contactIds,
  contactFilter,
  onOpenChange,
  onCreated,
  schedulingEnabled = true,
  editing,
}: {
  open: boolean;
  contactIds?: Identifier[];
  contactFilter?: ContactCohortFilter;
  onOpenChange: (open: boolean) => void;
  onCreated?: (broadcast: WhatsAppBroadcast) => void;
  schedulingEnabled?: boolean;
  /**
   * When set, the dialog edits this saved broadcast instead of creating one.
   * An edit always stays scheduled — the API refuses to dispatch from a PATCH —
   * so the send-mode choice is fixed to "later" for the whole session.
   */
  editing?: WhatsAppBroadcast | null;
}) {
  const isEditing = Boolean(editing);
  // An edit feeds its stored audience through the composer's own two audience
  // inputs, so create and edit share a single audience path. In create mode
  // these are exactly the incoming props.
  const editingAudience = useMemo(
    () => broadcastAudienceInputs(editing),
    [editing],
  );
  const effectiveContactIds = editing ? editingAudience.contactIds : contactIds;
  const effectiveContactFilter = editing
    ? editingAudience.contactFilter
    : contactFilter;
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [senderCatalog, setSenderCatalog] =
    useState<WhatsAppSenderCatalog | null>(null);
  const [senderKey, setSenderKey] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [audienceSelection, setAudienceSelection] =
    useState<ContactAudienceSelection | null>(null);
  const [audienceEstimate, setAudienceEstimate] =
    useState<ContactAudienceEstimate | null>(null);
  const [audienceResetKey, setAudienceResetKey] = useState(0);
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [headerMedia, setHeaderMedia] = useState<BroadcastHeaderMediaValue>({
    mode: "template",
    asset: null,
  });
  const [mediaUploadEnabled, setMediaUploadEnabled] = useState(false);
  const [sendMode, setSendMode] = useState<"now" | "scheduled">("now");
  const [scheduledFor, setScheduledFor] = useState("");
  const [complianceAttested, setComplianceAttested] = useState(false);
  const [includeNotOptedIn, setIncludeNotOptedIn] = useState(false);
  const [preview, setPreview] = useState<WhatsAppBroadcastPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [includedContinents, setIncludedContinents] = useState<
    ContactGeoCode[]
  >([]);
  const [excludedContinents, setExcludedContinents] = useState<
    ContactGeoCode[]
  >([]);
  const [selectedRatings, setSelectedRatings] = useState<number[]>([]);
  const [includeUnrated, setIncludeUnrated] = useState(false);
  const [ratingFilterEnabled, setRatingFilterEnabled] = useState(false);
  const [contactVariablesEnabled, setContactVariablesEnabled] = useState(false);

  const selectedSender = useMemo(
    () =>
      senderCatalog?.senders.find((sender) => sender.sender_key === senderKey),
    [senderCatalog, senderKey],
  );
  const availableTemplates = useMemo(
    () =>
      templates.filter((template) =>
        templateMatchesSender(template, selectedSender),
      ),
    [selectedSender, templates],
  );
  const selectedTemplate = useMemo(
    () =>
      availableTemplates.find((template) => String(template.id) === templateId),
    [availableTemplates, templateId],
  );
  const senderReady = Boolean(
    selectedSender && senderCanSend(selectedSender, "marketing"),
  );
  const initialAudienceKey = useMemo(
    () =>
      effectiveContactFilter
        ? JSON.stringify(effectiveContactFilter)
        : (effectiveContactIds ?? []).map(String).join("\n"),
    [effectiveContactFilter, effectiveContactIds],
  );
  const cohortMode = !!effectiveContactFilter && !effectiveContactIds?.length;
  const hasGeographyFilter =
    includedContinents.length > 0 || excludedContinents.length > 0;
  const effectiveSelectedRatings = useMemo(
    () => (ratingFilterEnabled ? selectedRatings : []),
    [ratingFilterEnabled, selectedRatings],
  );
  const effectiveIncludeUnrated = ratingFilterEnabled && includeUnrated;
  const hasRatingFilter =
    effectiveSelectedRatings.length > 0 || effectiveIncludeUnrated;
  const geographyListFilter = useMemo(
    () =>
      buildContactGeographyListFilter(includedContinents, excludedContinents),
    [excludedContinents, includedContinents],
  );
  const ratingListFilter = useMemo(
    () =>
      buildContactRatingListFilter(
        effectiveSelectedRatings,
        effectiveIncludeUnrated,
      ),
    [effectiveIncludeUnrated, effectiveSelectedRatings],
  );
  const selectorListFilter = useMemo(
    () => ({ ...geographyListFilter, ...ratingListFilter }),
    [geographyListFilter, ratingListFilter],
  );
  const excludedGeographyListFilter = useMemo(
    () => buildExcludedContactGeographyListFilter(excludedContinents),
    [excludedContinents],
  );
  const hasExplicitAudience =
    audienceSelection?.mode === "explicit" &&
    (audienceSelection.contact_ids?.length ?? 0) > 0;
  const geographyControlsDisabled = !cohortMode && hasExplicitAudience;
  const geographyOverlap = useMemo(
    () =>
      includedContinents.filter((code) => excludedContinents.includes(code)),
    [excludedContinents, includedContinents],
  );
  const refinementSelectsAllMatching =
    (Object.keys(geographyListFilter).length > 0 || hasRatingFilter) &&
    !geographyControlsDisabled &&
    !geographyOverlap.length;
  const effectiveAudienceSelection = useMemo(
    () =>
      applyAudienceRefinements(
        audienceSelection,
        includedContinents,
        excludedContinents,
        effectiveSelectedRatings,
        effectiveIncludeUnrated,
      ),
    [
      audienceSelection,
      excludedContinents,
      includedContinents,
      effectiveIncludeUnrated,
      effectiveSelectedRatings,
    ],
  );
  const effectiveCohortFilter = useMemo<ContactCohortFilter | null>(() => {
    if (effectiveAudienceSelection?.mode !== "filter") return null;
    return (effectiveAudienceSelection.filter ?? {}) as ContactCohortFilter;
  }, [effectiveAudienceSelection]);
  const cohortSummaryChips = useMemo(
    () =>
      effectiveCohortFilter ? cohortFilterLabelKeys(effectiveCohortFilter) : [],
    [effectiveCohortFilter],
  );
  const fields = useMemo(
    () => selectedTemplate?.parameter_schema ?? [],
    [selectedTemplate],
  );
  const mediaField = useMemo(() => fields.find(isMediaHeaderField), [fields]);
  const textFields = useMemo(
    () => fields.filter((field) => !isMediaHeaderField(field)),
    [fields],
  );
  const headerMediaFormat = useMemo<MediaHeaderFormat | null>(() => {
    if (!selectedTemplate) return null;
    const format = getHeaderMediaFormat(selectedTemplate);
    return format ? (format as MediaHeaderFormat) : null;
  }, [selectedTemplate]);
  const effectiveHeaderAssetId =
    headerMedia.mode === "override"
      ? headerMedia.asset?.id
      : selectedTemplate?.header_media_asset_id;
  // Show the chosen override image in the recipient mock-up (the backend preview
  // renders the template default, not the not-yet-saved override).
  const headerMediaPreviewUrl =
    headerMedia.mode === "override"
      ? headerMedia.asset?.preview_url
      : undefined;
  const mediaReady = !mediaField || !!effectiveHeaderAssetId;
  // 1-2★-only audiences are the angriest cohort: warn before MARKETING content
  // (template pausing risk; prefer a UTILITY service-recovery template).
  const lowRatingMarketingWarning =
    String(selectedTemplate?.category ?? "").toUpperCase() === "MARKETING" &&
    effectiveSelectedRatings.length > 0 &&
    !effectiveIncludeUnrated &&
    normalizeSelectedRatings(effectiveSelectedRatings).every(
      (star) => star <= 2,
    );
  const canPreview =
    senderReady &&
    !!selectedTemplate &&
    !geographyOverlap.length &&
    !!effectiveAudienceSelection &&
    (effectiveAudienceSelection.mode === "filter" ||
      (audienceEstimate?.count ?? 0) > 0 ||
      hasGeographyFilter ||
      hasRatingFilter);
  const scheduleValid =
    sendMode === "now" ||
    (!!scheduledFor && new Date(scheduledFor).getTime() > Date.now());
  const canCreate =
    !!preview &&
    !!preview.counts.eligible &&
    complianceAttested &&
    scheduleValid &&
    mediaReady;

  const loadTemplates = useCallback(async () => {
    try {
      const [nextTemplates, nextSenderCatalog] = await Promise.all([
        dataProvider.listWhatsAppTemplates(),
        dataProvider.getWhatsAppSenders(),
      ]);
      setTemplates(
        nextTemplates.filter(
          (template) => template.broadcast_eligible !== false,
        ),
      );
      setSenderCatalog(nextSenderCatalog);
      setSenderKey((current) => {
        if (
          nextSenderCatalog.senders.some(
            (sender) =>
              sender.sender_key === current &&
              senderCanSend(sender, "marketing"),
          )
        ) {
          return current;
        }
        return initialSenderKey(nextSenderCatalog, "marketing");
      });
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  }, [dataProvider, notify]);

  useEffect(() => {
    if (open) {
      void loadTemplates();
    }
  }, [loadTemplates, open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    void (async () => {
      try {
        const capabilities = await dataProvider.getDashboardCapabilities();
        if (active) {
          setMediaUploadEnabled(
            Boolean(capabilities?.features?.template_media_upload),
          );
          setRatingFilterEnabled(
            Boolean(capabilities?.features?.broadcast_rating_filter),
          );
          setContactVariablesEnabled(
            Boolean(capabilities?.features?.broadcast_contact_variables),
          );
        }
      } catch {
        // Capabilities are best-effort; override upload stays disabled.
      }
    })();
    return () => {
      active = false;
    };
  }, [dataProvider, open]);

  // Reset the header-media choice to the template default whenever the selected
  // template changes (imported templates with no stored asset start in upload).
  useEffect(() => {
    setHeaderMedia(defaultHeaderMediaValue(selectedTemplate));
  }, [selectedTemplate]);

  const uploadHeaderMedia = useCallback(
    (file: File): Promise<WhatsAppTemplateMediaAsset> =>
      fileToBase64(file).then((dataBase64) =>
        dataProvider.uploadWhatsAppTemplateMedia({
          file_name: file.name,
          mime_type: file.type || "application/octet-stream",
          data_base64: dataBase64,
          operator_user_id: identity?.id,
          operator_name: identity?.fullName,
        }),
      ),
    [dataProvider, identity?.fullName, identity?.id],
  );

  useEffect(() => {
    if (
      !availableTemplates.some((template) => String(template.id) === templateId)
    ) {
      setTemplateId(
        availableTemplates.length ? String(availableTemplates[0].id) : "",
      );
    }
  }, [availableTemplates, templateId]);

  useEffect(() => {
    if (open) {
      setAudienceResetKey((current) => current + 1);
      setAudienceSelection(
        cohortMode && effectiveContactFilter
          ? {
              mode: "filter",
              resource: "contacts",
              filter: effectiveContactFilter,
              sort: { field: "last_seen", order: "DESC" },
              excluded_contact_ids: [],
            }
          : null,
      );
      setAudienceEstimate(
        cohortMode
          ? {
              mode: "filter",
              count: 1,
              selectedAllMatching: true,
            }
          : null,
      );
      setIncludedContinents(effectiveContactFilter?.geo_continents_any ?? []);
      setExcludedContinents(effectiveContactFilter?.geo_continents_none ?? []);
      setSelectedRatings(
        normalizeSelectedRatings(effectiveContactFilter?.last_rating_any ?? []),
      );
      setIncludeUnrated(Boolean(effectiveContactFilter?.last_rating_none));
      setPreview(null);
    }
  }, [cohortMode, effectiveContactFilter, initialAudienceKey, open]);

  // Hydrate the form from the broadcast being edited — once per open, and only
  // after the template + sender catalogs have loaded. Hydrating earlier loses
  // the template: the "keep templateId valid" effect below clears any id that
  // is not in `availableTemplates`, which is empty until the catalogs land.
  // The ref keeps a later catalog refresh from overwriting operator edits.
  const hydratedBroadcastId = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !editing) {
      hydratedBroadcastId.current = null;
      return;
    }
    if (!templates.length || !senderCatalog) return;
    const key = String(editing.id);
    if (hydratedBroadcastId.current === key) return;
    hydratedBroadcastId.current = key;

    // The audience is NOT set here: the reset effect above plus the contact
    // selector already rebuild it from `effectiveContactIds`/
    // `effectiveContactFilter`, and setting it a second time here would fight
    // the selector's own onChange.
    const draft = broadcastEditDraft(editing);
    setName(draft.name);
    if (draft.senderKey) setSenderKey(draft.senderKey);
    if (draft.templateId) setTemplateId(draft.templateId);
    setParameters(draft.parameters);
    setSendMode("scheduled");
    setScheduledFor(draft.scheduledForInput);
    setIncludeNotOptedIn(draft.includeNotOptedIn);
    setComplianceAttested(draft.complianceAttested);
    setPreview(null);
  }, [editing, open, senderCatalog, templates.length]);

  // Invalidate a cached preview only when the inputs that shape it actually
  // change *by value*. Several of these (geographyOverlap, effectiveAudience-
  // Selection) are rebuilt into fresh references each render; using them as raw
  // effect deps fired this reset on every render and wiped a just-computed
  // preview, permanently disabling the Send button. See the regression test.
  const previewInvalidationKey = broadcastPreviewInvalidationKey({
    templateId,
    senderKey,
    audienceSelection: effectiveAudienceSelection,
    parameters,
    headerMedia,
    geographyOverlap,
    includeNotOptedIn,
    ratingFilter: {
      stars: normalizeSelectedRatings(effectiveSelectedRatings),
      unrated: effectiveIncludeUnrated,
    },
  });
  useEffect(() => {
    setPreview(null);
  }, [previewInvalidationKey]);

  useEffect(() => {
    if (hasExplicitAudience && hasGeographyFilter && !cohortMode) {
      setIncludedContinents([]);
      setExcludedContinents([]);
    }
  }, [cohortMode, hasExplicitAudience, hasGeographyFilter]);

  // Refinements only make sense on a filter audience: explicit contact picks
  // win, exactly like the geography force-clear above.
  useEffect(() => {
    if (hasExplicitAudience && hasRatingFilter && !cohortMode) {
      setSelectedRatings([]);
      setIncludeUnrated(false);
    }
  }, [cohortMode, hasExplicitAudience, hasRatingFilter]);

  const updateAudience = useCallback(
    (
      selection: ContactAudienceSelection,
      estimate: ContactAudienceEstimate,
    ) => {
      setAudienceSelection(selection);
      setAudienceEstimate(estimate);
    },
    [],
  );

  const buildPayload = () => {
    if (!selectedSender || !senderCanSend(selectedSender, "marketing")) {
      throw new Error("A ready marketing sender is required");
    }
    if (!selectedTemplate) throw new Error("Template is required");
    if (!effectiveAudienceSelection) throw new Error("Audience is required");
    return {
      sender_key: selectedSender.sender_key,
      name: name || `${selectedTemplate.name} broadcast`,
      template_id: selectedTemplate.id,
      template_name: selectedTemplate.name,
      template_language: selectedTemplate.language,
      audience: effectiveAudienceSelection,
      contact_ids:
        effectiveAudienceSelection.mode === "explicit"
          ? effectiveAudienceSelection.contact_ids
          : undefined,
      contact_filter:
        effectiveAudienceSelection.mode === "filter"
          ? effectiveAudienceSelection.filter
          : undefined,
      header_media_asset_id: effectiveHeaderAssetId ?? undefined,
      default_template_parameters: parameters,
      operator_user_id: identity?.id,
      operator_name: identity?.fullName,
      start_immediately: sendMode === "now",
      scheduled_for:
        sendMode === "scheduled" && scheduledFor
          ? new Date(scheduledFor).toISOString()
          : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      audience_source: cohortMode
        ? "cohort_filter"
        : effectiveAudienceSelection.mode === "filter"
          ? hasRatingFilter
            ? "rating_filter"
            : hasGeographyFilter
              ? "geography_filter"
              : "contact_selector_filter"
          : effectiveContactIds?.length
            ? "selected_contacts"
            : "contact_selector",
      audience_filter_snapshot: effectiveAudienceSelection,
      include_not_opted_in: includeNotOptedIn,
      operator_compliance_attestation: complianceAttested
        ? "operator_confirmed_opt_in_and_template_policy"
        : undefined,
    };
  };

  const previewBroadcast = async () => {
    setBusy(true);
    try {
      setPreview(await dataProvider.previewWhatsAppBroadcast(buildPayload()));
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const createBroadcast = async () => {
    setBusy(true);
    try {
      const payload = buildPayload();
      const broadcast = editing
        ? await dataProvider.updateWhatsAppBroadcast(editing.id, payload)
        : await dataProvider.createWhatsAppBroadcast(payload);
      notify(
        editing
          ? "crm.whatsapp.broadcasts.updated_success"
          : "crm.whatsapp.broadcasts.created_success",
        { type: "success" },
      );
      onCreated?.(broadcast);
      onOpenChange(false);
      setPreview(null);
      setName("");
      setAudienceSelection(null);
      setAudienceEstimate(null);
      setAudienceResetKey((current) => current + 1);
      setParameters({});
      setHeaderMedia({ mode: "template", asset: null });
      setScheduledFor("");
      setSendMode("now");
      setComplianceAttested(false);
      setIncludeNotOptedIn(false);
      setIncludedContinents([]);
      setExcludedContinents([]);
      setSelectedRatings([]);
      setIncludeUnrated(false);
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {translate(
              isEditing
                ? "crm.whatsapp.broadcasts.edit"
                : "crm.whatsapp.broadcasts.new",
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditing
              ? "Revise a scheduled WhatsApp template broadcast."
              : "Compose a WhatsApp template broadcast."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.broadcasts.name")}</Label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={translate(
                  "crm.whatsapp.broadcasts.name_placeholder",
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>Sender</Label>
              <Select value={senderKey} onValueChange={setSenderKey}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a ready sender" />
                </SelectTrigger>
                <SelectContent>
                  {(senderCatalog?.senders ?? []).map((sender) => (
                    <SelectItem
                      key={sender.sender_key}
                      value={sender.sender_key}
                      disabled={!senderCanSend(sender, "marketing")}
                    >
                      {senderLabel(sender)} · {sender.lifecycle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {senderCatalog && !senderReady && (
                <p className="text-xs text-destructive">
                  No verified marketing sender is selected.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.broadcasts.template")}</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableTemplates.map((template) => (
                    <SelectItem
                      key={String(template.id)}
                      value={String(template.id)}
                    >
                      {template.name} ({template.language})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Label>{translate("crm.whatsapp.broadcasts.recipients")}</Label>
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                <Globe2 className="size-4" />
                {translate("crm.whatsapp.broadcasts.cohort.geography")}
                {geographyControlsDisabled && (
                  <Badge variant="secondary">
                    {translate(
                      "crm.whatsapp.broadcasts.cohort.explicit_geo_disabled",
                    )}
                  </Badge>
                )}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <ContinentToggleButtons
                  label={translate("crm.whatsapp.broadcasts.cohort.include")}
                  selected={includedContinents}
                  disabled={busy || geographyControlsDisabled}
                  onChange={setIncludedContinents}
                />
                <ContinentToggleButtons
                  label={translate("crm.whatsapp.broadcasts.cohort.exclude")}
                  selected={excludedContinents}
                  disabled={busy || geographyControlsDisabled}
                  onChange={setExcludedContinents}
                />
              </div>
              {!!geographyOverlap.length && (
                <Badge variant="destructive">
                  {translate("crm.whatsapp.broadcasts.cohort.geo_overlap")}
                </Badge>
              )}
              {ratingFilterEnabled && (
                <>
                  <Separator />
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <Star className="size-4" />
                    {translate("crm.whatsapp.broadcasts.cohort.rating")}
                    {geographyControlsDisabled && (
                      <Badge variant="secondary">
                        {translate(
                          "crm.whatsapp.broadcasts.cohort.explicit_rating_disabled",
                        )}
                      </Badge>
                    )}
                  </div>
                  <RatingToggleButtons
                    selectedRatings={selectedRatings}
                    includeUnrated={includeUnrated}
                    disabled={busy || geographyControlsDisabled}
                    onRatingsChange={setSelectedRatings}
                    onIncludeUnratedChange={setIncludeUnrated}
                  />
                  {lowRatingMarketingWarning && (
                    <Badge variant="destructive">
                      {translate(
                        "crm.whatsapp.broadcasts.cohort.detractor_marketing_warning",
                      )}
                    </Badge>
                  )}
                </>
              )}
            </div>
            {cohortMode ? (
              <div className="flex flex-wrap gap-2 rounded-md border p-3">
                <Badge variant="secondary">
                  {translate("crm.whatsapp.broadcasts.cohort.selected_segment")}
                </Badge>
                {cohortSummaryChips.map((chip) => (
                  <Badge key={chip.key} variant="outline">
                    {translate(chip.key, chip.options)}
                  </Badge>
                ))}
              </div>
            ) : (
              <ContactAudienceSelector
                initialContactIds={effectiveContactIds}
                resetKey={audienceResetKey}
                disabled={busy}
                listFilter={selectorListFilter}
                excludedListFilter={excludedGeographyListFilter}
                selectAllMatchingByDefault={refinementSelectsAllMatching}
                onChange={updateAudience}
              />
            )}
          </div>

          {selectedTemplate && (
            <LiveMessagePreview
              headerMediaUrlOverride={headerMediaPreviewUrl}
              parameters={parameters}
              template={selectedTemplate}
            />
          )}

          {((mediaField && headerMediaFormat && selectedTemplate) ||
            textFields.length > 0) && (
            <div className="grid gap-3">
              {mediaField && headerMediaFormat && selectedTemplate && (
                <BroadcastHeaderMediaField
                  template={selectedTemplate}
                  format={headerMediaFormat}
                  value={headerMedia}
                  onChange={setHeaderMedia}
                  mediaUploadEnabled={mediaUploadEnabled}
                  onUpload={uploadHeaderMedia}
                />
              )}
              {textFields.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {textFields.map((field) => (
                    <div className="space-y-2" key={field.key}>
                      <div className="flex items-center justify-between gap-2">
                        <Label>{field.label || field.key}</Label>
                        {contactVariablesEnabled && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                              >
                                <Plus className="size-3" />
                                {translate(
                                  "crm.whatsapp.broadcasts.insert_variable",
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {CONTACT_TEMPLATE_VARIABLES.map((variable) => (
                                <DropdownMenuItem
                                  key={variable.name}
                                  onSelect={() =>
                                    setParameters((current) => ({
                                      ...current,
                                      [field.key]: `${
                                        current[field.key] ?? ""
                                      }${contactVariableToken(
                                        variable.name,
                                        variable.defaultFallback,
                                      )}`,
                                    }))
                                  }
                                >
                                  {translate(variable.labelKey)}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <Input
                        value={parameters[field.key] ?? ""}
                        onChange={(event) =>
                          setParameters({
                            ...parameters,
                            [field.key]: event.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {preview && (
            <>
              <Separator />
              <div className="flex flex-wrap gap-2">
                <Badge>{preview.counts.eligible} eligible</Badge>
                <Badge variant="secondary">
                  {preview.counts.skipped} skipped
                </Badge>
                <Badge variant="secondary">{preview.counts.total} total</Badge>
                {!!preview.counts.included_without_opt_in && (
                  <Badge variant="outline">
                    {translate(
                      "crm.whatsapp.broadcasts.included_without_opt_in",
                      { count: preview.counts.included_without_opt_in },
                    )}
                  </Badge>
                )}
              </div>
              {!!preview.audience_resolution && (
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline">
                    {translate("crm.whatsapp.broadcasts.cohort.scanned", {
                      count:
                        preview.audience_resolution.contacts_scanned ??
                        preview.counts.total,
                    })}
                  </Badge>
                  <Badge variant="outline">
                    {translate("crm.whatsapp.broadcasts.cohort.pages", {
                      count: preview.audience_resolution.pages_scanned ?? 0,
                    })}
                  </Badge>
                  {!preview.audience_resolution.scan_complete && (
                    <Badge variant="destructive">
                      {translate("crm.whatsapp.broadcasts.cohort.incomplete")}
                    </Badge>
                  )}
                </div>
              )}
              {!!Object.keys(preview.geo_counts ?? {}).length && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(preview.geo_counts ?? {}).map(
                    ([code, counts]) => (
                      <Badge key={code} variant="secondary">
                        {counts.label ?? code}: {counts.eligible}/{counts.total}
                      </Badge>
                    ),
                  )}
                </div>
              )}
              {!!preview.warnings?.length && (
                <div className="grid gap-2">
                  {preview.warnings.map((warning) => (
                    <div
                      key={warning.code}
                      className="rounded-md border border-destructive/50 px-3 py-2 text-sm text-destructive"
                    >
                      {warning.message}
                    </div>
                  ))}
                </div>
              )}
              {Object.keys(preview.skip_reasons ?? {}).length > 0 && (
                <div className="text-sm text-muted-foreground">
                  {Object.entries(preview.skip_reasons)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {!!preview.skip_reasons?.missing_template_variable && (
                <p className="text-sm text-muted-foreground">
                  {translate("crm.whatsapp.broadcasts.missing_variable_hint", {
                    count: preview.skip_reasons.missing_template_variable,
                  })}
                </p>
              )}
              <RenderedPreview
                preview={preview}
                translate={translate}
                fallbackParameters={parameters}
                headerMediaUrlOverride={headerMediaPreviewUrl}
              />
            </>
          )}

          <Separator />

          <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.broadcasts.send_mode")}</Label>
              <Select
                value={sendMode}
                onValueChange={(value) =>
                  setSendMode(value === "scheduled" ? "scheduled" : "now")
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Editing never dispatches — the API refuses to send from a
                      PATCH — so "now" is not offered on a saved broadcast. */}
                  <SelectItem value="now" disabled={isEditing}>
                    {translate("crm.whatsapp.broadcasts.send_now")}
                  </SelectItem>
                  <SelectItem
                    value="scheduled"
                    disabled={!schedulingEnabled && !isEditing}
                  >
                    {translate("crm.whatsapp.broadcasts.send_later")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                {translate("crm.whatsapp.broadcasts.scheduled_for")}
              </Label>
              <Input
                type="datetime-local"
                value={scheduledFor}
                disabled={sendMode !== "scheduled"}
                min={toDatetimeLocal(new Date())}
                onChange={(event) => setScheduledFor(event.target.value)}
              />
            </div>
          </div>

          <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
            <Checkbox
              checked={includeNotOptedIn}
              onCheckedChange={(checked) =>
                setIncludeNotOptedIn(checked === true)
              }
            />
            <span className="space-y-1">
              <span className="block">
                {translate(
                  "crm.whatsapp.broadcasts.include_not_opted_in_label",
                )}
              </span>
              <span className="block text-xs text-muted-foreground">
                {translate("crm.whatsapp.broadcasts.include_not_opted_in_hint")}
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
            <Checkbox
              checked={complianceAttested}
              onCheckedChange={(checked) =>
                setComplianceAttested(checked === true)
              }
            />
            <span>
              {translate("crm.whatsapp.broadcasts.compliance_attestation")}
            </span>
          </label>

          {preview &&
            !preview.counts.eligible &&
            !!preview.skip_reasons?.not_opted_in &&
            !includeNotOptedIn && (
              <p className="text-sm text-muted-foreground">
                {translate(
                  "crm.whatsapp.broadcasts.send_blocked_not_opted_in_hint",
                  { count: preview.skip_reasons.not_opted_in },
                )}
              </p>
            )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            variant="outline"
            disabled={!canPreview || busy}
            onClick={() => void previewBroadcast()}
          >
            <Eye className="size-4" />
            {translate("crm.whatsapp.broadcasts.preview")}
          </Button>
          <Button
            disabled={!canCreate || busy}
            onClick={() => void createBroadcast()}
          >
            {sendMode === "scheduled" ? (
              <CalendarClock className="size-4" />
            ) : (
              <Send className="size-4" />
            )}
            {translate(
              isEditing
                ? "crm.whatsapp.broadcasts.save_changes"
                : sendMode === "scheduled"
                  ? "crm.whatsapp.broadcasts.schedule"
                  : "crm.whatsapp.broadcasts.send",
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function applyAudienceRefinements(
  selection: ContactAudienceSelection | null,
  includedContinents: ContactGeoCode[],
  excludedContinents: ContactGeoCode[],
  selectedRatings: number[],
  includeUnrated: boolean,
): ContactAudienceSelection | null {
  const hasRatingRefinement = selectedRatings.length > 0 || includeUnrated;
  if (
    !includedContinents.length &&
    !excludedContinents.length &&
    !hasRatingRefinement
  ) {
    return selection;
  }
  if (selection?.mode === "explicit" && selection.contact_ids?.length) {
    return selection;
  }

  let filter = {
    ...(selection?.mode === "filter" ? selection.filter : { all: true }),
  } as ContactCohortFilter;
  if (includedContinents.length) {
    filter.geo_continents_any = includedContinents;
  } else {
    delete filter.geo_continents_any;
  }
  if (excludedContinents.length) {
    filter.geo_continents_none = excludedContinents;
  } else {
    delete filter.geo_continents_none;
  }
  filter = applyRatingToCohortFilter(filter, selectedRatings, includeUnrated);

  return {
    mode: "filter",
    resource: "contacts",
    filter,
    sort:
      selection?.mode === "filter"
        ? selection.sort
        : { field: "last_seen", order: "DESC" },
    included_contact_ids:
      selection?.mode === "explicit" ? selection.contact_ids : undefined,
    excluded_contact_ids:
      selection?.mode === "filter" ? selection.excluded_contact_ids : [],
  };
}

function RatingToggleButtons({
  selectedRatings,
  includeUnrated,
  disabled = false,
  onRatingsChange,
  onIncludeUnratedChange,
}: {
  selectedRatings: number[];
  includeUnrated: boolean;
  disabled?: boolean;
  onRatingsChange: (next: number[]) => void;
  onIncludeUnratedChange: (next: boolean) => void;
}) {
  const translate = useTranslate();
  const { data: facetsData } = useContactFilterFacets();
  return (
    <div className="flex flex-wrap gap-1.5">
      {RATING_STAR_OPTIONS.map((star) => {
        const active = selectedRatings.includes(star);
        const count = facetCount(
          facetsData?.facets,
          "last_rating",
          String(star),
        );
        return (
          <Button
            key={star}
            type="button"
            variant={active ? "secondary" : "outline"}
            size="sm"
            disabled={disabled}
            onClick={() =>
              onRatingsChange(
                active
                  ? selectedRatings.filter((value) => value !== star)
                  : [...selectedRatings, star],
              )
            }
          >
            {star} ★{count != null ? ` (${count})` : ""}
          </Button>
        );
      })}
      <Button
        type="button"
        variant={includeUnrated ? "secondary" : "outline"}
        size="sm"
        disabled={disabled}
        onClick={() => onIncludeUnratedChange(!includeUnrated)}
      >
        {translate("crm.whatsapp.broadcasts.cohort.rating_unrated")}
      </Button>
    </div>
  );
}

function ContinentToggleButtons({
  label,
  selected,
  disabled = false,
  onChange,
}: {
  label: string;
  selected: ContactGeoCode[];
  disabled?: boolean;
  onChange: (next: ContactGeoCode[]) => void;
}) {
  const translate = useTranslate();
  return (
    <div className="space-y-2">
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {CONTINENT_OPTIONS.map((option) => {
          const active = selected.includes(option.code);
          return (
            <Button
              key={option.code}
              type="button"
              variant={active ? "secondary" : "outline"}
              size="sm"
              disabled={disabled}
              onClick={() =>
                onChange(toggleCode(selected, option.code) as ContactGeoCode[])
              }
            >
              {translate(option.labelKey, { _: option.fallback })}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "crm.whatsapp.error";
}

function toDatetimeLocal(date: Date) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function RenderedPreview({
  preview,
  translate,
  fallbackParameters,
  headerMediaUrlOverride,
}: {
  preview: WhatsAppBroadcastPreview;
  translate: ReturnType<typeof useTranslate>;
  fallbackParameters: Record<string, string>;
  headerMediaUrlOverride?: string | null;
}) {
  const recipients = preview.recipients.slice(0, 5);
  const remaining = Math.max(0, preview.counts.total - recipients.length);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <MessageSquareText className="size-4" />
        {translate("crm.whatsapp.broadcasts.message_preview")}
      </div>
      <div className="grid gap-2">
        {recipients.map((recipient) => (
          <RecipientPreview
            key={String(recipient.id)}
            recipient={recipient}
            template={preview.template}
            fallbackParameters={fallbackParameters}
            headerMediaUrlOverride={headerMediaUrlOverride}
          />
        ))}
      </div>
      {remaining > 0 && (
        <p className="text-xs text-muted-foreground">
          {translate("crm.whatsapp.broadcasts.more_previews", {
            count: remaining,
          })}
        </p>
      )}
    </div>
  );
}

/**
 * The message exactly as a recipient will see it, re-rendered on every
 * keystroke.
 *
 * This is deliberately client-side: the composer previously showed only the raw
 * template body with `{{1}}` placeholders, and the real mock-up — substituted
 * values, header media, buttons — needed a round trip through the Preview
 * button. Rendering it live is what makes writing a broadcast feel like writing
 * a message.
 *
 * The per-recipient preview below is still server-rendered, because only the
 * backend resolves contact variables against real contacts.
 */
function LiveMessagePreview({
  template,
  parameters,
  headerMediaUrlOverride,
}: {
  template: WhatsAppTemplate;
  parameters: Record<string, string>;
  headerMediaUrlOverride?: string | null;
}) {
  const translate = useTranslate();
  // Contact variables have no value until the backend resolves them per
  // recipient, so show their fallback text rather than raw {{contact.*}}.
  const resolved = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(parameters).map(([key, value]) => [
          key,
          describeVariableTokens(value),
        ]),
      ),
    [parameters],
  );
  const message = renderTemplateMessageParts(template, resolved);
  const buttons = getTemplateButtons(template);
  const missing = unfilledPlaceholders(template, resolved);

  return (
    <div className="grid gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MessageSquareText className="size-4" />
          {translate("crm.whatsapp.broadcasts.live_preview")}
        </div>
        {missing.length > 0 && (
          <Badge variant="outline" className="text-amber-600">
            {translate("crm.whatsapp.broadcasts.live_preview_missing", {
              placeholders: missing.join(" "),
            })}
          </Badge>
        )}
      </div>
      <WhatsAppMessageBubble
        body={message.body}
        buttons={buttons}
        className="max-w-md"
        emptyBodyLabel={translate("crm.whatsapp.broadcasts.live_preview_empty")}
        footer={message.footer}
        header={message.header}
        media={templateMediaFor(template, resolved, headerMediaUrlOverride)}
      />
      {buttons.some((button) => button.type !== "URL") && (
        <div className="grid gap-1">
          {buttons
            .filter((button) => button.type !== "URL")
            .map((button) => (
              <ButtonIntentHint key={button.text} label={button.text} />
            ))}
        </div>
      )}
    </div>
  );
}

function templateMediaFor(
  template: WhatsAppTemplate,
  parameters: Record<string, string>,
  urlOverride?: string | null,
) {
  const format = getHeaderMediaFormat(template);
  if (!format) return null;
  return {
    format,
    url:
      urlOverride ||
      parameters.header_media_url?.trim() ||
      template.header_media_url,
    label: template.header_media_file_name || format,
  };
}

function RecipientPreview({
  recipient,
  template,
  fallbackParameters,
  headerMediaUrlOverride,
}: {
  recipient: WhatsAppBroadcastPreviewRecipient;
  template: WhatsAppTemplate;
  fallbackParameters: Record<string, string>;
  headerMediaUrlOverride?: string | null;
}) {
  // The backend preview resolves contact-variable tokens per recipient; the
  // fallback path (no per-recipient values yet) renders tokens as their
  // fallback text so the mock-up never shows raw {{contact.*}} syntax.
  const parameters =
    recipient.template_parameters ??
    Object.fromEntries(
      Object.entries(fallbackParameters).map(([key, value]) => [
        key,
        describeVariableTokens(value),
      ]),
    );
  const message = renderTemplateMessageParts(template, parameters);
  const label =
    recipient.contact_name ||
    recipient.whatsapp_user_id ||
    String(recipient.contact_id);

  return (
    <div className="rounded-md border bg-background p-3 text-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 font-medium">{label}</div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {(recipient.phone_continent_name ||
            recipient.phone_continent_code) && (
            <Badge variant="outline">
              {recipient.phone_continent_name || recipient.phone_continent_code}
            </Badge>
          )}
          <Badge
            variant={recipient.status === "eligible" ? "default" : "secondary"}
          >
            {recipient.status}
          </Badge>
        </div>
      </div>
      <WhatsAppMessageBubble
        body={message.body}
        buttons={getTemplateButtons(template)}
        emptyBodyLabel={template.name}
        footer={message.footer}
        header={message.header}
        media={templateMediaFor(template, parameters, headerMediaUrlOverride)}
      />
      {!!recipient.skip_reasons?.length && (
        <p className="mt-2 text-xs text-muted-foreground">
          {recipient.skip_reasons.join(", ")}
        </p>
      )}
    </div>
  );
}
